import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Manages page-specific meta tags for better search engine optimization
 * Optimized for top 20 search keywords for coffee shops in Nadi, Fiji
 * 
 * TOP SEARCH KEYWORDS TARGETED:
 * 1. best coffee shop Nadi
 * 2. coffee near me (Nadi/Fiji)
 * 3. cafe Nadi Fiji
 * 4. breakfast Nadi
 * 5. best cafe Nadi
 * 6. coffee shop near Nadi airport
 * 7. Bulaccino Nadi
 * 8. smoothie bowl Nadi
 * 9. acai bowl Fiji
 * 10. best breakfast Nadi
 * 11. top cafes Nadi
 * 12. coffee Fiji
 * 13. cafe near me Fiji
 * 14. Namaka cafe
 * 15. best coffee Fiji
 * 16. vegan cafe Nadi
 * 17. gluten free Nadi
 * 18. brunch Nadi
 * 19. where to eat Nadi
 * 20. coffee shop Fiji
 */
const SEO = ({ 
  title, 
  description, 
  path = '', 
  canonical = '',  // NEW: Accept full canonical URL
  image = '/og-image.jpg',
  type = 'website',
  keywords = '',
  noindex = false 
}) => {
  const siteUrl = 'https://bulaccinocafe.com';
  
  // Handle canonical URL - prioritize explicit canonical, then build from path
  let fullUrl;
  if (canonical) {
    // If full canonical URL provided, use it directly
    fullUrl = canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`;
  } else if (path) {
    // Build from path
    fullUrl = `${siteUrl}${path.startsWith('/') ? path : '/' + path}`;
  } else {
    // Fallback: try to get current path from window
    fullUrl = typeof window !== 'undefined' 
      ? `${siteUrl}${window.location.pathname}` 
      : siteUrl;
  }
  
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Core keywords targeting top searches
  const coreKeywords = 'best coffee shop Nadi, coffee near me, cafe Nadi Fiji, breakfast Nadi, best cafe Nadi, coffee shop near Nadi airport, Bulaccino Nadi, smoothie bowl Nadi, acai bowl Fiji, best breakfast Nadi, top cafes Nadi, coffee Fiji, cafe near me Fiji, Namaka cafe, best coffee Fiji, vegan cafe Nadi, gluten free Nadi, brunch Nadi, where to eat Nadi, coffee shop Fiji';
  const allKeywords = keywords ? `${keywords}, ${coreKeywords}` : coreKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={fullUrl} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Bulaccino Cafe Nadi" />
      <meta property="og:locale" content="en_FJ" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
    </Helmet>
  );
};

export default SEO;

// Pre-defined SEO configurations optimized for top search keywords
export const SEO_CONFIG = {
  home: {
    title: 'Bulaccino Cafe Nadi | Best Coffee Shop in Fiji | Breakfast & Smoothies Near Airport',
    description: 'Bulaccino Cafe - Voted best coffee shop in Nadi, Fiji! Fresh brewed coffee, smoothie bowls, acai bowls & delicious breakfast. Vegan & gluten-free options. Located in Namaka near Nadi Airport. Open 7AM-6PM. Order online!',
    keywords: 'best coffee Nadi 2026, top rated cafe Fiji, recommended coffee shop Nadi, must visit cafe Fiji'
  },
  menu: {
    title: 'Menu | Bulaccino Cafe Nadi | Coffee, Breakfast, Smoothies & Acai Bowls',
    description: 'View Bulaccino Cafe\'s full menu - fresh coffee, espresso drinks, tropical smoothie bowls, acai bowls, breakfast wraps & lunch. Vegan & gluten-free options. Order online for pickup in Nadi!',
    keywords: 'Bulaccino menu, cafe menu Nadi, coffee prices Fiji, breakfast menu Nadi, smoothie menu, acai bowl menu Fiji, vegan menu Nadi'
  },
  buildYourOwn: {
    title: 'Build Your Own Breakfast & Bowls | Bulaccino Cafe Nadi | Customize Your Meal',
    description: 'Create your perfect meal at Bulaccino Cafe Nadi! Build your own breakfast, customize your smoothie bowl, or design your perfect wrap. Fresh ingredients, vegan options, endless possibilities.',
    keywords: 'custom breakfast Nadi, build your own bowl Fiji, customizable cafe food, personalized breakfast'
  },
  order: {
    title: 'Order Online | Bulaccino Cafe Nadi | Quick Pickup Near Airport',
    description: 'Order your favorite coffee, smoothies & breakfast online from Bulaccino Cafe Nadi. Fast pickup in Namaka, near Nadi Airport. Pay with M-PAiSA. Skip the line - order now!',
    keywords: 'order coffee online Nadi, cafe pickup Fiji, online food order Namaka, quick breakfast Nadi airport'
  },
  about: {
    title: 'About Bulaccino Cafe | Best Coffee Shop in Nadi, Fiji | Our Story',
    description: 'Discover Bulaccino Cafe - Nadi\'s favorite coffee shop since opening. Fresh local ingredients, island hospitality & passion for great coffee. Voted best cafe in Fiji by locals and travelers.',
    keywords: 'about Bulaccino Cafe, cafe story Fiji, Nadi coffee history, local cafe Fiji'
  },
  contact: {
    title: 'Contact & Location | Bulaccino Cafe | Shop 3 Concave Road Namaka Nadi Fiji',
    description: 'Visit Bulaccino Cafe: Shop 3, Concave Road, Namaka, Nadi, Fiji. Open daily 7AM-6PM. WhatsApp: +679 675 0102. 10 minutes from Nadi Airport. Free parking. Find directions & hours.',
    keywords: 'Bulaccino location, cafe directions Nadi, how to get to Bulaccino, Namaka coffee shop address, cafe near Nadi airport, parking Bulaccino'
  },
  loyalty: {
    title: 'Rewards Program | Bulaccino Cafe Nadi | Earn Free Coffee & Discounts',
    description: 'Join Bulaccino Cafe\'s loyalty rewards program! Earn points on every coffee, smoothie & breakfast order. Get free drinks, exclusive discounts & birthday treats. Sign up free today!',
    keywords: 'cafe rewards Nadi, loyalty program Fiji, free coffee rewards, coffee points program, Bulaccino rewards'
  },
  careers: {
    title: 'Careers at Bulaccino Cafe Nadi | Jobs in Fiji | Now Hiring',
    description: 'Join Bulaccino Cafe team in Nadi, Fiji! Now hiring: Chef, Barista, Server, Cashier & more. Great work environment, competitive pay. Apply today - email career@bulaccinocafe.com',
    keywords: 'cafe jobs Nadi, barista jobs Fiji, restaurant careers Nadi, hiring coffee shop Fiji, jobs in Nadi'
  },
  favorites: {
    title: 'My Favorites | Bulaccino Cafe Nadi',
    description: 'View and reorder your favorite items from Bulaccino Cafe. Quick access to your go-to coffee, smoothies & breakfast items.',
    keywords: ''
  },
  orderHistory: {
    title: 'Order History | Bulaccino Cafe Nadi',
    description: 'View your past orders from Bulaccino Cafe Nadi. Easily reorder your favorite coffee, smoothies & breakfast.',
    keywords: ''
  },
  privacyPolicy: {
    title: 'Privacy Policy | Bulaccino Cafe Nadi, Fiji',
    description: 'Bulaccino Cafe privacy policy. Learn how we protect your personal information when you order online or join our rewards program.',
    keywords: ''
  },
  termsOfUse: {
    title: 'Terms of Use | Bulaccino Cafe Nadi, Fiji',
    description: 'Terms of service for Bulaccino Cafe website and online ordering platform in Nadi, Fiji.',
    keywords: ''
  },
  cookies: {
    title: 'Cookie Policy | Bulaccino Cafe Nadi, Fiji',
    description: 'Cookie policy for Bulaccino Cafe website. Learn how we use cookies to improve your browsing experience.',
    keywords: ''
  },
  press: {
    title: 'Press & Media Kit | Bulaccino Cafe Nadi | Company Info & Media Resources',
    description: 'Official press kit for Bulaccino Cafe. Founded June 1, 2025 in Nadi, Fiji. Download logos, photos, and company facts. Media inquiries: media@bulaccinocafe.com',
    keywords: 'Bulaccino Cafe press, Fiji cafe media, Nadi coffee shop news, Bulaccino founder, cafe press release Fiji'
  },
  partners: {
    title: 'Our Partners | Cafe Planet Coffee & Votualevu Organic Farm | Bulaccino Cafe',
    description: 'Discover Bulaccino Cafe\'s local Fijian partners: Cafe Planet specialty coffee roasters from Korotogo and Votualevu Organic Farm\'s 30-acre sustainable farm with 116 fruit trees.',
    keywords: 'Cafe Planet Fiji, Votualevu Farm, Korotogo coffee roaster, organic farm Fiji, local suppliers Nadi, Coral Coast coffee, sustainable cafe Fiji'
  },
  faq: {
    title: 'FAQ | Bulaccino Cafe Nadi | Hours, Location, Menu Questions Answered',
    description: 'Frequently asked questions about Bulaccino Cafe in Nadi, Fiji. Hours: 6:30 AM - 8:00 PM daily. Location: Shop 3, Concave Road, Namaka. Vegan & gluten-free options available. Free parking.',
    keywords: 'Bulaccino FAQ, cafe questions Nadi, coffee shop hours Fiji, parking Bulaccino, vegan options Nadi cafe'
  }
};
